/**
 * Reducer for watch video tutorials. Triggers a watch video tutorial if
 * the user gets a question wrong twice in a row and has not already
 * completed the tutorial
 */

import { fromJS } from 'immutable';
import log from 'loglevel';

import { QUESTION_ACTIVITY_FINISHED } from 'phoenix2/state/question-activity/actions';
import { EVALUATIONS } from 'phoenix2/state/question-activity/question-activity';
import { SET_TUTORIALS_COMPLETED, RESET_TUTORIALS } from '../actions';

export const WATCH_VIDEO_TUTORIAL_KEY = 'watchVideoTutorial';

const INITIAL_STATE = fromJS({
    completed: false,

    // Disabled by default until a user gets a question wrong twice
    active: false,
    state: {
        // List of task item ids that the user got wrong
        itemsAnsweredWrong: [],

        // List of task item ids for which a "show video" tutorial
        // should be shown (i.e. user got them wrong twice)
        showTutorialItems: [],
    },
});

export function watchVideoTutorialReducer(state = INITIAL_STATE, action) {
    switch (action.type) {
        case RESET_TUTORIALS:
            return INITIAL_STATE;

        case SET_TUTORIALS_COMPLETED:
            if (action.tutorialIds.indexOf(WATCH_VIDEO_TUTORIAL_KEY) !== -1) {
                return fromJS({
                    completed: true,
                    active: false,
                    state: null,
                });
            }
            break;

        case QUESTION_ACTIVITY_FINISHED:
            return triggerWatchVideoTutorial(state, action);
    }

    return state;
}

/**
 * Given a QUESTION_ACTIVITY_FINISHED action, trigger a watch video tutorial if
 * the criteria are met: the tutorial should not already have been completed,
 * and the user must have just made a wrong attempt at a task, and had a
 * least one previous incorrect attempts.
 * The task item id is recorded in the tutorial state so that the tutorial can
 * point to it
 * @param state the redux state
 * @param action a DEEP_UPDATE_TASK_ITEM_STATE action
 * @returns {*}
 */
function triggerWatchVideoTutorial(state, action) {
    // The watch video tutorial is only ever shown once so never
    // triggered if already completed:
    if (state.get('completed')) {
        return state;
    }

    const taskItemId = action.questionActivity.get('taskItemId');

    let itemsAnsweredWrong = state.getIn(['state', 'itemsAnsweredWrong']);
    let showTutorialItems = state.getIn(['state', 'showTutorialItems']);

    if (action.evaluation === EVALUATIONS.wrong) {
        if (showTutorialItems.includes(taskItemId)) {
            // Already triggered tutorial for this item - do nothing
            return state;
        } else if (itemsAnsweredWrong.includes(taskItemId)) {
            log.debug('Watch video tutorial triggered');

            // Second time answered wrong, so trigger tutorial:
            showTutorialItems = showTutorialItems.push(taskItemId);

            const index = itemsAnsweredWrong.indexOf(taskItemId);
            if (index === -1) {
                itemsAnsweredWrong = itemsAnsweredWrong.delete(taskItemId);
            }

            state = fromJS({
                completed: false,
                active: true,
                state: {
                    showTutorialItems,
                    itemsAnsweredWrong,
                },
            });
        } else {
            // First time the item was answered wrong - add to list:
            itemsAnsweredWrong = itemsAnsweredWrong.push(taskItemId);
            state = state.setIn(['state', 'itemsAnsweredWrong'], itemsAnsweredWrong);
        }
    } else if (action.evaluation === EVALUATIONS.correct) {
        // Task item correct - make sure it is not on the list of wrong
        // answers any more
        const index = itemsAnsweredWrong.indexOf(taskItemId);
        if (index === -1) {
            return state;
        }
        itemsAnsweredWrong = itemsAnsweredWrong.delete(index);
        state = state.setIn(['state', 'itemsAnsweredWrong'], itemsAnsweredWrong);
    } else {
        // Question not answered - don't change tutorial state
        return state;
    }

    return state;
}
